import { useState } from 'react'
import { BottomSheet } from 'components/Mobile/BottomSheet'
import FbButton from 'components/FbUI/FbButton'
import { useReviewSampleRequest } from 'services/samples'

export type ReviewMode = 'accept' | 'reject'

interface Props {
  mode: ReviewMode
  sampleId: number
  /** Human-friendly identifier shown in the confirm copy. */
  sampleRequestNumber: string
  onClose: () => void
  /** Fired after a successful mutation; parent should close + refresh. */
  onSuccess: () => void
}

/**
 * Discriminated-union payload accepted by `reviewSampleRequest`.
 * Exported separately from `buildReviewPayload` for use in callers / tests.
 */
export type ReviewPayload =
  | { status: 'accepted' }
  | { status: 'rejected'; rejection_reason: string }

/**
 * Whether the primary "Confirm" button should be enabled. Accept always
 * confirms; reject requires a non-empty (after trim) reason.
 */
export function canConfirm(mode: ReviewMode, reason: string): boolean {
  if (mode === 'accept') return true
  return reason.trim().length > 0
}

/**
 * Build the exact payload object passed to `reviewSampleRequest`. Trims
 * whitespace around the rejection reason so callers don't have to.
 */
export function buildReviewPayload(
  mode: ReviewMode,
  reason: string
): ReviewPayload {
  if (mode === 'accept') {
    return { status: 'accepted' }
  }
  return { status: 'rejected', rejection_reason: reason.trim() }
}

/**
 * Bottom-sheet confirm UX for the broker-facing sample review flow.
 *
 * Mode-aware:
 * - `accept` — single confirm step, no extra inputs.
 * - `reject` — adds a required textarea for the rejection reason.
 *
 * On success, invalidates the three caches that drive the samples surface
 * (detail, infinite list, header counts) and fires `onSuccess` so the
 * parent can dismiss the sheet and refresh its own UI.
 */
export function MobileSampleReviewSheet({
  mode,
  sampleId,
  sampleRequestNumber,
  onClose,
  onSuccess,
}: Props) {
  const [reason, setReason] = useState('')

  // Cache invalidation (detail + infinite list + header counts) is handled
  // declaratively by the samples invalidation registry.
  const mutation = useReviewSampleRequest()

  const handleConfirm = () => {
    mutation.mutate(
      { id: sampleId, data: buildReviewPayload(mode, reason) },
      {
        onSuccess: () => {
          setReason('')
          onSuccess()
        },
      }
    )
  }

  const isAccept = mode === 'accept'
  const title = isAccept ? 'Approve sample request' : 'Reject sample request'
  const body = isAccept
    ? `Request #${sampleRequestNumber} will be sent to customer service for fulfillment.`
    : `The broker will be notified that request #${sampleRequestNumber} was rejected, along with the reason below.`

  const confirmLabel = isAccept ? 'Confirm approval' : 'Confirm rejection'
  const pendingLabel = isAccept ? 'Approving…' : 'Rejecting…'
  const disabled = !canConfirm(mode, reason) || mutation.isPending

  const handleClose = () => {
    if (mutation.isPending) return
    setReason('')
    mutation.reset()
    onClose()
  }

  return (
    <BottomSheet onClose={handleClose} title={title}>
      <p className="text-sm text-fg-secondary">{body}</p>

      {!isAccept && (
        <textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for rejection"
          className="mt-3 w-full rounded-md border border-line-form bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-line-focus"
        />
      )}

      <div className="mt-5 flex flex-col gap-2">
        <FbButton
          type="button"
          variant={isAccept ? 'primary' : 'danger'}
          size="lg"
          className="w-full"
          disabled={disabled}
          onClick={handleConfirm}
        >
          {mutation.isPending ? pendingLabel : confirmLabel}
        </FbButton>
        <FbButton
          type="button"
          variant="transparent"
          size="lg"
          className="w-full"
          disabled={mutation.isPending}
          onClick={handleClose}
        >
          Cancel
        </FbButton>
      </div>

      {mutation.isError && (
        <p className="mt-3 text-sm text-error">
          Couldn&apos;t complete review. Check your connection and try again.
        </p>
      )}
    </BottomSheet>
  )
}
